// CSP nonce setup - MUST be imported first before styled-components
import './cspNonceSetup';

import { FeatureFlagsProvider } from '@common/feature-flags/FeatureFlags';
import { listenForNetwork } from '@common/sentry';
import { getSqlIDEUrl, isAuthRoute, isEmbeddedSingUpUrl, isPublicURL } from '@common/url';
import { event as fullStoryEvent, getCurrentSessionURL as fullStoryGetCurrentSessionURL } from '@fullstory/browser';
import { ObjectLiteral } from '@prophecy/interfaces/generic';
import { isNodeEvnProduction } from '@prophecy/utils/env';
import { applyExpensiveCssOverrides, getIsExpensiveCssDisabled } from '@prophecy/utils/expensiveCss';
import { historyReplace, navigateAndRefresh } from '@prophecy/utils/history';
import { fullStoryIntegration } from '@sentry/fullstory';
import { init as sentryInit, reactRouterV6BrowserTracingIntegration } from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import { StyleSheetManager } from 'styled-components';

import { AppMetadataProvider } from './context/appMetadata';
import { getAppMetadata } from './context/getAppMetadata';
import { MixPanelProvider } from './context/mixpanel/context';
import { AppMetadataProviderType, UserDetails } from './context/types';
import { fetchControlCentreAuth, fetchUsageMetrics, postUsageMetrics, updateTimeStamp } from './data/apis/athena';
import { ControlCenterResponse, UsgaeMetricsResponse } from './data/apis/types';
import { redirectToAuth } from './data/rest/redirectToAuth';
import { isShareUrl } from './data/rest/util';
import { makeGraphQlCallWithoutShareTokens } from './data/util';
import { DevTool } from './devtool';
import { MainApp } from './MainApp';
import { cloneSharedProjectMutation } from './pages/Sql/Replay/Share/queries';
import { getReplayId, isSharedReplay } from './pages/Sql/Replay/Share/replayDetails';
import { getShareId, isSharedProject } from './pages/Sql/Replay/Share/shareDetails';
import { getGraphQlEndPoint } from './utils/getServerUrl';
import { isReplayMode } from './utils/replayToken';
import { isShareMode, shareToken } from './utils/shareToken';
import { GlobalWindow } from './utils/types';
import { isNewUser } from './utils/user';
import 'reactflow/dist/style.css';
import '@common/appVersion';

const SASS_DOMAINS = ['app.prophecy.io', 'app.prophecy.ai'];

if (isNodeEvnProduction() && SASS_DOMAINS.includes(window.location.host)) {
  // store util to get fullstory session url on global window, so while testing folks can directly get it from console and forward
  (window as unknown as GlobalWindow).getFullStorySessionURL = fullStoryGetCurrentSessionURL;

  console.warn('Configuring Sentry');
  sentryInit({
    dsn: 'https://80b67469d06c4f53a053992a41b4bdf8@o449849.ingest.sentry.io/5433542',
    integrations: [
      reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      fullStoryIntegration('prophecy', {
        client: { event: fullStoryEvent, getCurrentSessionURL: fullStoryGetCurrentSessionURL }
      })
    ],
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 1.0,
    beforeBreadcrumb(breadcrumb) {
      // filter out default graphql breadcrumb as we have custom breadcrumb for graphql
      return breadcrumb.category === 'fetch' && getGraphQlEndPoint() === breadcrumb.data?.url ? null : breadcrumb;
    }
  });
  listenForNetwork();
}

const returnMainApp = (data: AppMetadataProviderType, isLoggedIn: boolean) => {
  return (
    <StyleSheetManager>
      <FeatureFlagsProvider>
        <AppMetadataProvider value={data}>
          <MixPanelProvider>
            <MainApp isLoggedIn={isLoggedIn} userId={data.user?.id as string} />
          </MixPanelProvider>
        </AppMetadataProvider>
        <DevTool />
      </FeatureFlagsProvider>
    </StyleSheetManager>
  );
};

const updateUserMetrics = async () => {
  try {
    const metricsResponse = (await fetchUsageMetrics()) as UsgaeMetricsResponse;
    if (!metricsResponse?.data?.aggregateMetrics) {
      return;
    }
    const { data } = (await fetchControlCentreAuth()) as ControlCenterResponse;
    const { url, key } = data;
    if (!url || !key) {
      return;
    }

    const updateUsageMetricsResponse = (await postUsageMetrics(
      url,
      { aggregateMetrics: metricsResponse?.data?.aggregateMetrics },
      {
        Authorization: key
      }
    )) as ObjectLiteral;

    if (updateUsageMetricsResponse?.code === 200) {
      updateTimeStamp({ requestID: metricsResponse?.data?.requestID });
    }
  } catch (e) {
    console.error('error while updating usage metrics', e);
  }
};

// This function is used to clone the shared project when the user signup from the shared project
const handleAutoClone = async (isLoggedIn: boolean, user?: UserDetails) => {
  const searchParams = new URLSearchParams(window.location.search);
  const autoClone = searchParams.get('autoClone') === 'true';
  const projectId = shareToken.shareDetails?.projectUID;

  // If auto clone is enabled or the user is new, clone the shared project
  if ((autoClone || (user && isNewUser(user))) && isLoggedIn && projectId) {
    try {
      const {
        cloneSharedProject: { _id }
      } = await makeGraphQlCallWithoutShareTokens(cloneSharedProjectMutation, {
        uid: String(projectId),
        fabricUID: String(shareToken.shareDetails?.fabricUID ?? '')
      });
      const newUrl = getSqlIDEUrl({ uid: _id, onboarding: 'true', remix: 'true' });
      navigateAndRefresh(newUrl);
      return true;
    } catch (error) {
      console.error('Auto clone failed', error);
    }
  }
  return false;
};

const run = async () => {
  let pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  const shareId = getShareId(pathname, searchParams);
  const replayId = getReplayId(pathname, searchParams);
  const data = await getAppMetadata(shareId, replayId);
  const isExpensiveCssDisabled = getIsExpensiveCssDisabled(data.globalConfig);
  applyExpensiveCssOverrides(isExpensiveCssDisabled);
  // if share-token/replay-token is present, consider user as logged in
  // for embed url consider user as non logged in
  const isLoggedIn = (Boolean(data?.user?.id) && !isEmbeddedSingUpUrl(pathname)) || isShareMode() || isReplayMode();

  if (data.shareUrl) {
    // This condition also protects the changes in url path for shared project
    if (!isSharedProject(data.shareUrl)) {
      historyReplace(data.shareUrl);
    }
  }

  if (data.replayUrl) {
    // This condition also protects the changes in url path for replay
    if (!isSharedReplay(data.replayUrl)) {
      historyReplace(data.replayUrl);
    }
  }

  // check if we need to clone the shared project for user
  await handleAutoClone(isLoggedIn, data.user);

  pathname = window.location.pathname;

  if (!isLoggedIn && !isAuthRoute(pathname) && !isPublicURL(pathname) && !isShareUrl()) {
    redirectToAuth(true);
  } else if (isLoggedIn && isAuthRoute(pathname)) {
    if (!shareToken.get()) {
      historyReplace('/');
    }
  }

  if (isLoggedIn && data.globalConfig['prophecy.ui.usageMetrics.enabled']) {
    updateUserMetrics();
  }

  const rootElement = document.getElementById('root');
  if (rootElement) {
    ReactDOM.createRoot(rootElement).render(returnMainApp(data, isLoggedIn));
  } else {
    throw new Error('Could not mount react app as element #root was not found');
  }
};

run();
